import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "What Happens If You Don't Reach Full Support? A Missionary's Options";
const DESCRIPTION =
    "A practical, honest look at what actually happens when a missionary hits their departure date without full support — the real options, how sending organizations handle it, and how to keep raising support without losing momentum.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover17.jpg',
            datePublished: '2026-07-29',
            dateModified: '2026-07-29',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/what-happens-if-you-dont-reach-full-support',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: "What percentage of support do most sending organizations require before departure?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It varies by organization, but many require somewhere between 75% and 100% of your monthly budget pledged before they'll approve a departure date, with a smaller number allowing partial deployment at 60-70% if a clear plan exists to close the gap on the field.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I still go to the field if I only reach partial support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Sometimes, depending on your organization's policy and how close you are — a common path is a delayed departure with a firm re-evaluation date, or an approved partial deployment paired with a continued support-raising plan run alongside your field work.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it a bad sign if I need to extend my support-raising timeline?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No — extended timelines are common and rarely reflect on your calling or fitness for the field. Support raising is affected by factors outside your control, including your network's season of life and the broader economy, not just your effort.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I lower my support goal if I am struggling to reach it?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Only after re-checking your budget line by line with your organization's finance team — a goal built on a realistic, itemized budget shouldn't be cut just because it feels hard to reach; the fix is usually a wider or better-worked network, not a smaller number.",
                    },
                },
            ],
        },
    ],
};

export default function WhatHappensIfYouDontReachFullSupport() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/what-happens-if-you-dont-reach-full-support' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-what-happens-full-support-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover17.jpg" alt="A missionary reviewing their support-raising progress before a departure date" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most missionaries who don't reach 100% support by their target departure date assume the worst — that it means something went wrong, or that the door is closing. In practice, falling short of a deadline is one of the most common and least catastrophic parts of support raising. What actually happens next depends far more on your sending organization's policy and your own plan than on the shortfall itself.</p>

                    <h2>The three most common outcomes</h2>

                    <p><strong>A delayed departure with a new target date.</strong> This is the most common path. Your organization sets a revised deadline, usually 60-90 days out, and you keep raising support against a specific, realistic number rather than an open-ended goal. A firm new date tends to re-energize a stalled campaign more than an indefinite "keep going" would.</p>

                    <p><strong>Partial deployment with a continued-raising plan.</strong> Many organizations allow missionaries to deploy at 70-85% support if there's a credible plan to close the remaining gap within a set window on the field — often paired with a temporary reduction in discretionary budget lines. This isn't available everywhere, so check your organization's specific threshold early rather than assuming it applies to you.</p>

                    <p><strong>A reassessment of the budget or role.</strong> Occasionally, a persistent shortfall is a signal to revisit the budget itself — not necessarily to cut it, but to check for line items that were estimated high, or a role/location combination that changed cost assumptions since the original budget was set. See <a href="/resources/how-much-support-does-a-missionary-need">how much support does a missionary need</a> for a framework on building a realistic number in the first place.</p>

                    <h2>What sending organizations are actually looking for</h2>

                    <p>Support percentage thresholds exist to protect missionaries from arriving on the field financially exposed, not to punish slow fundraising. When you're short, most organizations want to see two things: a realistic, itemized budget (not padded, not thin) and evidence of an active, still-growing pipeline of asks — not just people who've already said no. A missionary at 70% with fifteen pending conversations is usually in a stronger position than one at 85% whose list has gone cold.</p>

                    <h2>How to keep momentum after a missed deadline</h2>

                    <p>A missed target date is a natural point where enthusiasm dips — for you and for the people who've already committed. Counter it directly: send a short, honest update to current supporters about the revised timeline (this builds trust more than it damages it), and treat the new date as a fresh campaign with its own follow-up rhythm rather than a continuation of a stalled one. If your network feels tapped out, revisit <a href="/resources/raising-support-with-no-network">how to raise support when you don't have a big network</a> for ways to expand past your original list.</p>

                    <h2>When to seriously reconsider timing</h2>

                    <p>A single missed deadline is normal. A pattern of missed deadlines over 6+ months with a flat or declining pledge total is worth a direct conversation with your sending organization about whether the timeline, the role, or the budget needs to change — not because you've failed, but because an honest reset is more useful than repeatedly pushing the same date.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What percentage of support do most sending organizations require before departure?</strong><br />
                    Typically 75-100%, though some allow partial deployment at 60-70% with a plan to close the gap.</p>

                    <p><strong>Can I still go to the field if I only reach partial support?</strong><br />
                    Sometimes — it depends on your organization's policy, often paired with a delayed departure or a continued-raising plan.</p>

                    <p><strong>Is it a bad sign if I need to extend my support-raising timeline?</strong><br />
                    No, extensions are common and rarely reflect on your calling or effort.</p>

                    <p><strong>Should I lower my support goal if I am struggling to reach it?</strong><br />
                    Only after re-checking the underlying budget — the fix is usually a wider network, not a smaller number.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-much-support-does-a-missionary-need">How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown</a></li>
                        <li><a href="/resources/how-long-to-raise-full-support-as-a-missionary">How Long Does It Take to Raise Full Support as a Missionary?</a></li>
                        <li><a href="/resources/raising-support-with-no-network">How to Raise Missionary Support When You Don't Have a Big Network</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this after hearing the same fear from missionary after missionary — that a missed deadline meant the calling was wrong. It almost never does.</p>
                    <p><small><em>Last updated: 2026-07-29.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/what-happens-if-you-dont-reach-full-support" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
